import React, { useEffect, useState } from "react";

import { Form, Formik } from "formik";
import uniq from "lodash/uniq";
import { useDispatch, useSelector } from "react-redux";

import { RuleAction } from "actions";

import { Button, SecondaryButton } from "components/Form";
import { useSubmitHandler } from "components/Form/FormikControls";
import { DialogActions, DialogContent } from "components/MaterialDialog";

import { ModalTypes } from "constants/navigation";
import { RuleBookPermission } from "constants/permissions";

import { convertToExternalRule } from "containers/Settings/RuleBooks/export";
import {
  convertToInternalRule,
  getParentInputDataType,
} from "containers/Settings/RuleBooks/import";
import { RuleForm } from "containers/Settings/RuleBooks/Rules/CustomRules";
import { resolveParentDataType } from "containers/Settings/RuleBooks/Rules/CustomRules/lib";
import { selectPrerequisiteRuleChainByRuleIdLookup } from "containers/Settings/RuleBooks/Schema/livestockSaleSchema";
import {
  RuleFieldSchema,
  useRuleFieldSchema,
} from "containers/Settings/RuleBooks/Schema/schemaContext";

import {
  getContextByModalType,
  getLastOrderByRuleBookId,
  getRuleBookById,
  getRuleById,
  getRulesByRuleBookId,
  getRuleSets,
  selectBillingTagOptions,
} from "selectors";

import { useHasPermission } from "hooks";

export function extractCategoryNames(rules) {
  return uniq(rules.map(rule => (rule.category || "").trim()));
}

export function EditCustomRuleForm(props) {
  const { onClose, ruleBookId } = props;
  const dispatch = useDispatch();

  const context = useSelector(getContextByModalType(ModalTypes.EditRule)) || {};
  const { ruleId = null, rule: templateRule = null } = context;

  useEffect(() => {
    ruleId && dispatch(RuleAction.requestOne({ id: ruleId }));
  }, [dispatch, ruleId]);
  const rule = useSelector(getRuleById(ruleId));

  const otherRulesInRuleBook =
    useSelector(getRulesByRuleBookId(ruleBookId)) || [];

  const ruleSetByIdLookup = useSelector(getRuleSets);

  const schema = useRuleFieldSchema();

  const ruleChainLookup = useSelector(
    selectPrerequisiteRuleChainByRuleIdLookup,
  );

  const categories = extractCategoryNames(
    otherRulesInRuleBook.map(externalRule => JSON.parse(externalRule.content)),
  );

  const lastOrder = useSelector(getLastOrderByRuleBookId(ruleBookId));

  function onSubmit(values) {
    const updatedParentDataType = resolveParentDataType(
      values.prerequisite_rule_ids,
      ruleChainLookup,
      values.rule_set_id,
      ruleSetByIdLookup,
      schema,
    );
    const externalRule = convertToExternalRule(
      { order: lastOrder + 1, ...values },
      schema,
      updatedParentDataType,
    );

    if (ruleId) {
      dispatch(
        RuleAction.update(externalRule, {
          changeReason: "Updated from Rule Modal",
        }),
      );
    } else {
      dispatch(
        RuleAction.create(externalRule, {
          changeReason: "Created from Rule Modal",
        }),
      );
    }
    onClose();
  }

  // Get the initial/existing parent data type for this rule
  const ruleChain = ruleChainLookup[ruleId] || null;
  const parentDataType = getParentInputDataType(
    // The Rule will determine its own data type, it only needs to be given the path of the prerequisite rule/rulesets (when present)
    ruleChain?.inputPathParts.slice(0, -1),
    schema,
  );

  const initialValues = {
    apply_splits: true,
    apply_criteria_filter_to_splits: false,
    ...(rule ? convertToInternalRule(rule, schema, parentDataType) : null),
    ...templateRule,
    rule_book_id: ruleBookId,
  };

  const billingTags = useSelector(selectBillingTagOptions);

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      <Form data-tour="customRuleForm">
        <DialogContent>
          <RuleFieldSchema value={false}>
            <RuleForm
              categories={categories}
              billingTags={billingTags}
              ruleId={ruleId}
            />
          </RuleFieldSchema>
        </DialogContent>
        <DialogActions data-tour="customRuleFormActions">
          <Footer onClose={onClose} ruleId={ruleId} ruleBookId={ruleBookId} />
        </DialogActions>
      </Form>
    </Formik>
  );
}

function Footer(props) {
  const { onClose, ruleBookId } = props;
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);

  useSubmitHandler(true, setIsSubmitEnabled);

  const hasUpdateRuleBookPermission = useHasPermission(
    getRuleBookById(ruleBookId),
    RuleBookPermission.update,
  );

  return (
    <>
      <SecondaryButton type="button" onClick={onClose}>
        Close
      </SecondaryButton>
      <Button
        type="submit"
        data-tour="save"
        disabled={!(isSubmitEnabled && hasUpdateRuleBookPermission)}
        title={
          !hasUpdateRuleBookPermission
            ? "You do not have permission to update Rules in this Rule Book"
            : "Update Rule"
        }
      >
        Save
      </Button>
    </>
  );
}
